'use client'

import React from 'react';
import Section from "@/components/partials/section";
import Link from 'next/link';

type TableSection = {
  title?: string;
  description?: string;
  headers: string[];
  rows: {
    [key: string]: string;
  }[];
};

type TableProps = {
  id?: string;
  leading?: string;
  title?: string;
  description?: string;
  content: TableSection[];
  buttonText?: string;
  buttonHref?: string;
  onButtonClick?: () => void;
  className?: string;
};

const Table: React.FC<TableProps> = ({
  id = 'table-section',
  leading,
  title,
  description,
  content,
  buttonText,
  buttonHref,
  onButtonClick,
  className = ''
}) => {
  const handleClick = () => {
    if (typeof onButtonClick === 'function') {
      onButtonClick();
    }
  };

  return (
    <Section
      id={id}
      className={className}
      header={{
        classGroup: {
          h1: "text-3xl sm:text-6xl",
        },
        leading,
        title,
        description
      }}
    >
      {buttonText && (
        <div className="mb-6 flex justify-end">
          {buttonHref ? (
            <Link
              href={buttonHref}
              className="inline-block px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              {buttonText}
            </Link>
          ) : (
            <button
              onClick={handleClick}
              className="inline-block px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              {buttonText}
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 sm:gap-12">
        {content.map((section, idx) => (
          <div key={idx}>
            {section.title && (
              <h2 className="text-xl font-semibold mb-2">
                {section.title}
              </h2>
            )}
            {section.description && (
              <p className="text-lg text-[var(--body-1-color)] mb-6">
                {section.description}
              </p>
            )}
            <div className="relative overflow-x-auto rounded-lg border border-neutral-950">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-500 uppercase bg-neutral-950">
                  <tr>
                    {section.headers.map((header, i) => (
                      <th key={i} scope="col" className="px-6 py-3">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className="bg-[var(--background2-color)] border-b border-neutral-800 hover:bg-neutral-950"
                    >
                      {section.headers.map((key, colIdx) => (
                        <td
                          key={colIdx}
                          className={`px-6 py-4 ${colIdx === 0
                            ? 'font-medium text-purple-400 whitespace-nowrap'
                            : ''
                            }`}
                        >
                          {(() => {
                            const normalized = key.toLowerCase().replace(/\s+/g, '');
                            const headerKeyMap: Record<string, string> = {
                              // Common synonyms mapping for table headers → row fields
                              estimatedrate: 'estimatedpricerange',
                              // Add more mappings as needed
                            };
                            const resolvedKey = headerKeyMap[normalized] ?? normalized;
                            return row[resolvedKey] ?? '-';
                          })()}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

export default Table;
